import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  MdAccountBalance, MdAssignment, MdBusinessCenter, MdClose, MdDashboard,
  MdEmail, MdExpandLess, MdExpandMore, MdInbox, MdLogout, MdMenu,
  MdNotifications, MdOutlineMarkEmailRead, MdPeople, MdSend, MdSettings, MdStar,
} from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import { getMailUnreadCounts } from '../../services/adminMailService';
import ieumLogo from '../../assets/ieum-logo.png';

const MENU_ICONS = {
  MdEmail,
  MdDashboard,
  MdPeople,
  MdAssignment,
  MdAccountBalance,
  MdNotifications,
  MdBusinessCenter,
  MdSettings,
};

const STAFF_QUICK_KEYS = ['mail-inbox', 'mail-shared', 'mail-compose', 'mail-important', 'mail-settings'];
const STAFF_QUICK_ICONS = {
  'mail-inbox': MdInbox,
  'mail-shared': MdOutlineMarkEmailRead,
  'mail-compose': MdSend,
  'mail-important': MdStar,
  'mail-settings': MdSettings,
};

const DesktopNav = styled.nav`
  width: 250px;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.sidebarBg};
  color: ${({ theme }) => theme.colors.sidebarText};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 72px;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  img {
    width: 36px;
    height: 36px;
    object-fit: contain;
  }

  div { min-width: 0; }
  strong {
    display: block;
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text};
  }
  span {
    display: block;
    margin-top: 1px;
    font-size: 11px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const MenuList = styled.ul`
  flex: 1;
  min-height: 0;
  padding: 12px 0;
  overflow-y: auto;
`;

const MenuItem = styled.li``;

const MenuIcon = styled.span`
  display: inline-flex;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
`;

const MenuLink = styled(NavLink)`
  display: flex;
  min-height: 44px;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.sidebarText};
  transition: background 0.15s, color 0.15s;

  &:hover { background: ${({ theme }) => theme.colors.sidebarHover}; }
  &.active {
    color: #fff;
    background: ${({ theme }) => theme.colors.sidebarActive};
    border-radius: 8px;
    margin: 0 8px;
    padding: 10px 12px;
  }
`;

const ParentButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  padding: 10px 20px;
  font-size: 14px;
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.sidebarText)};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  background: ${({ $active, theme }) => ($active ? theme.colors.borderLight : 'transparent')};
  transition: background 0.15s, color 0.15s;

  &:hover { background: ${({ theme }) => theme.colors.sidebarHover}; }
`;

const SubList = styled.ul`
  padding: 2px 0 6px 24px;
`;

const UnreadBadge = styled.span`
  display: inline;
  color: ${({ theme }) => theme.colors.danger};
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
`;

const ExpandIcon = styled.span`
  display: inline-flex;
  margin-left: auto;
`;

const SubLink = styled(NavLink)`
  display: flex;
  min-height: 40px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 20px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: background 0.15s, color 0.15s;

  &:hover { background: ${({ theme }) => theme.colors.sidebarHover}; }
  &.active { color: ${({ theme }) => theme.colors.primary}; font-weight: 600; }
`;

const Footer = styled.div`
  padding: 14px 20px calc(14px + env(safe-area-inset-bottom));
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const UserName = styled.div`
  margin-bottom: 8px;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LogoutBtn = styled.button`
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  gap: 7px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;

  &:hover { color: ${({ theme }) => theme.colors.text}; }
`;

const MobileHeader = styled.header`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    position: fixed;
    inset: 0 0 auto;
    z-index: 900;
    display: flex;
    min-height: calc(58px + env(safe-area-inset-top));
    padding-top: env(safe-area-inset-top);
    align-items: center;
    background: ${({ theme }) => theme.colors.bgWhite};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    box-shadow: ${({ theme }) => theme.shadow.sm};
  }
`;

const MobileHeaderInner = styled.div`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 44px;
  align-items: center;
  width: 100%;
  max-width: 480px;
  min-height: 58px;
  margin: 0 auto;
  padding: 0 10px;
`;

const MobileMenuButton = styled.button`
  display: inline-flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};

  &:hover { background: ${({ theme }) => theme.colors.sidebarHover}; }
`;

const MobileBrand = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 8px;

  img { width: 28px; height: 28px; object-fit: contain; }
  div { min-width: 0; }
  strong {
    display: block;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.text};
    font-size: 14px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  span {
    display: block;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 10px;
    text-align: center;
  }
`;

const MobileUnread = styled.span`
  justify-self: center;
  color: ${({ theme }) => theme.colors.danger};
  font-size: 12px;
  font-weight: 700;
`;

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: none;
  background: rgba(26, 26, 26, 0.45);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: block;
  }
`;

const MobileDrawer = styled.aside`
  display: flex;
  flex-direction: column;
  width: min(86vw, 320px);
  height: 100%;
  padding-top: env(safe-area-inset-top);
  background: ${({ theme }) => theme.colors.bgWhite};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  animation: admin-drawer-in 0.18s ease-out;

  @keyframes admin-drawer-in {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  min-height: 64px;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 10px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const DrawerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  img { width: 32px; height: 32px; object-fit: contain; }
  strong { display: block; color: ${({ theme }) => theme.colors.text}; font-size: 15px; }
  span { display: block; margin-top: 2px; color: ${({ theme }) => theme.colors.textSecondary}; font-size: 11px; }
`;

const DrawerCloseButton = styled.button`
  display: inline-flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover { background: ${({ theme }) => theme.colors.sidebarHover}; }
`;

const StaffQuickNav = styled.nav`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    position: fixed;
    inset: auto 0 0;
    z-index: 850;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    min-height: calc(62px + env(safe-area-inset-bottom));
    padding: 4px 4px env(safe-area-inset-bottom);
    background: ${({ theme }) => theme.colors.bgWhite};
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    box-shadow: 0 -2px 10px rgba(31, 41, 55, 0.06);
  }
`;

const StaffQuickLink = styled(NavLink)`
  display: flex;
  min-width: 0;
  min-height: 56px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 2px;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 10px;
  font-weight: 500;

  &.active {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.borderLight};
    font-weight: 700;
  }
`;

const Sidebar = ({ menu, title = '이음' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, signOut } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mailUnread, setMailUnread] = useState(null);
  const [mailUnreadByMenu, setMailUnreadByMenu] = useState(null);
  const [openKeys, setOpenKeys] = useState(() => {
    const found = menu.find((item) => item.children?.some((child) => location.pathname.startsWith(child.path)));
    return found ? [found.key] : [];
  });
  const isStaff = userData?.role === 'staff';

  const activeLabel = useMemo(() => {
    for (const item of menu) {
      if (!item.children && location.pathname === item.path) return item.label;
      const child = item.children?.find((entry) => location.pathname.startsWith(entry.path));
      if (child) return child.label;
    }
    return isStaff ? '받은편지함' : '관리자';
  }, [isStaff, location.pathname, menu]);

  const staffQuickItems = useMemo(() => {
    const mailChildren = menu.find((item) => item.key === 'mail')?.children || [];
    return STAFF_QUICK_KEYS.map((key) => mailChildren.find((item) => item.key === key)).filter(Boolean);
  }, [menu]);

  const toggle = (key) => {
    setOpenKeys((previous) => previous.includes(key)
      ? previous.filter((item) => item !== key)
      : [...previous, key]);
  };

  useEffect(() => {
    setDrawerOpen(false);
    const found = menu.find((item) => item.children?.some((child) => location.pathname.startsWith(child.path)));
    if (found) {
      setOpenKeys((previous) => previous.includes(found.key) ? previous : [...previous, found.key]);
    }
  }, [location.pathname, location.search, menu]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!['admin', 'staff'].includes(userData?.role)) return undefined;
    let active = true;
    const refresh = async () => {
      try {
        const result = await getMailUnreadCounts();
        if (active) {
          setMailUnread(result.unread || 0);
          setMailUnreadByMenu({
            'mail-inbox': result.personal?.unread || 0,
            'mail-shared': result.sharedUnread || 0,
            'mail-important': result.important?.unread || 0,
          });
        }
      } catch {
        if (active) {
          setMailUnread(null);
          setMailUnreadByMenu(null);
        }
      }
    };
    void refresh();
    let eventTimer = null;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 60000);
    const onChanged = () => {
      window.clearTimeout(eventTimer);
      eventTimer = window.setTimeout(() => { void refresh(); }, 250);
    };
    const onVisibilityChanged = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener('ieum-mail-counts-changed', onChanged);
    document.addEventListener('visibilitychange', onVisibilityChanged);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.clearTimeout(eventTimer);
      window.removeEventListener('ieum-mail-counts-changed', onChanged);
      document.removeEventListener('visibilitychange', onVisibilityChanged);
    };
  }, [userData?.role]);

  const handleLogout = async () => {
    try {
      setDrawerOpen(false);
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error(error);
    }
  };

  const renderMenu = () => menu.map((item) => {
    const Icon = MENU_ICONS[item.icon];
    if (!item.children) {
      return (
        <MenuItem key={item.key}>
          <MenuLink to={item.path} end={item.path === '/admin' || item.path === '/partner'}>
            {Icon && <MenuIcon><Icon size={19} /></MenuIcon>}
            <span>{item.label}</span>
          </MenuLink>
        </MenuItem>
      );
    }

    const isOpen = openKeys.includes(item.key);
    const isActive = item.children.some((child) => location.pathname.startsWith(child.path));
    return (
      <MenuItem key={item.key}>
        <ParentButton type="button" $active={isActive} aria-expanded={isOpen} onClick={() => toggle(item.key)}>
          {Icon && <MenuIcon><Icon size={19} /></MenuIcon>}
          <span>{item.label}</span>
          {item.key === 'mail' && mailUnread > 0 && <UnreadBadge>{mailUnread > 99 ? '99+' : mailUnread}</UnreadBadge>}
          <ExpandIcon>{isOpen ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}</ExpandIcon>
        </ParentButton>
        {isOpen && (
          <SubList>
            {item.children.map((child) => (
              <li key={child.key}>
                <SubLink to={child.path}>
                  <span>{child.label}</span>
                  {mailUnreadByMenu?.[child.key] > 0 && <UnreadBadge>{mailUnreadByMenu[child.key] > 99 ? '99+' : mailUnreadByMenu[child.key]}</UnreadBadge>}
                </SubLink>
              </li>
            ))}
          </SubList>
        )}
      </MenuItem>
    );
  });

  const renderFooter = () => (
    <Footer>
      {userData && <UserName title={userData.name || userData.email}>{userData.name || userData.email}</UserName>}
      <LogoutBtn type="button" onClick={handleLogout}><MdLogout size={18} />로그아웃</LogoutBtn>
    </Footer>
  );

  return (
    <>
      <DesktopNav aria-label="관리자 메뉴">
        <Logo>
          <img src={ieumLogo} alt="이음" />
          <div><strong>{title}</strong><span>{isStaff ? '직원 메일' : '운영 관리'}</span></div>
        </Logo>
        <MenuList>{renderMenu()}</MenuList>
        {renderFooter()}
      </DesktopNav>

      <MobileHeader>
        <MobileHeaderInner>
          <MobileMenuButton type="button" aria-label="관리자 메뉴 열기" aria-expanded={drawerOpen} aria-controls="admin-mobile-drawer" onClick={() => setDrawerOpen(true)}>
            <MdMenu size={25} />
          </MobileMenuButton>
          <MobileBrand>
            <img src={ieumLogo} alt="" />
            <div><strong>{activeLabel}</strong><span>{isStaff ? '이음 메일' : '이음 관리자'}</span></div>
          </MobileBrand>
          <MobileUnread aria-label={mailUnread > 0 ? `읽지 않은 메일 ${mailUnread}개` : undefined}>{mailUnread > 0 ? (mailUnread > 99 ? '99+' : mailUnread) : ''}</MobileUnread>
        </MobileHeaderInner>
      </MobileHeader>

      {drawerOpen && (
        <MobileOverlay role="presentation" onClick={() => setDrawerOpen(false)}>
          <MobileDrawer id="admin-mobile-drawer" role="dialog" aria-modal="true" aria-label="관리자 메뉴" onClick={(event) => event.stopPropagation()}>
            <DrawerHeader>
              <DrawerTitle>
                <img src={ieumLogo} alt="이음" />
                <div><strong>{title}</strong><span>{isStaff ? '메일 확인 중심 직원 화면' : '전체 관리자 기능'}</span></div>
              </DrawerTitle>
              <DrawerCloseButton type="button" aria-label="관리자 메뉴 닫기" onClick={() => setDrawerOpen(false)}><MdClose size={24} /></DrawerCloseButton>
            </DrawerHeader>
            <MenuList>{renderMenu()}</MenuList>
            {renderFooter()}
          </MobileDrawer>
        </MobileOverlay>
      )}

      {isStaff && (
        <StaffQuickNav aria-label="직원 메일 빠른 메뉴">
          {staffQuickItems.map((item) => {
            const Icon = STAFF_QUICK_ICONS[item.key];
            return (
              <StaffQuickLink key={item.key} to={item.path}>
                {Icon && <Icon size={21} />}
                <span>{item.label.replace('메일함', '')}</span>
                {mailUnreadByMenu?.[item.key] > 0 && <UnreadBadge>{mailUnreadByMenu[item.key] > 99 ? '99+' : mailUnreadByMenu[item.key]}</UnreadBadge>}
              </StaffQuickLink>
            );
          })}
        </StaffQuickNav>
      )}
    </>
  );
};

export default Sidebar;
